import produce from 'immer';
import React, { createContext, Dispatch, useContext, useReducer } from 'react';

import {
  OwnershipConnectionsContextActions,
  OwnershipConnectionsContextActionTypes,
  OwnershipConnectionsProps,
  OwnershipConnectionsProviderProps,
} from './OwnershipConnectionsContext.types';

const OwnershipConnectionsContext = createContext<OwnershipConnectionsProps | undefined>(undefined);
const OwnershipConnectionsDispatchContext = createContext<
  Dispatch<OwnershipConnectionsContextActions> | undefined
>(undefined);

const initialConnectionsState: OwnershipConnectionsProps = {
  companyId: '',
  onlyBeneficialOwnership: false,
  includeHistoricalOwnership: false,
  path: [],
};

const OwnershipConnectionsReducer = (
  state: OwnershipConnectionsProps,
  action: OwnershipConnectionsContextActions,
) => {
  switch (action.type) {
    case OwnershipConnectionsContextActionTypes.SetCompanyId: {
      return produce(state, (draft) => {
        draft.companyId = action.payload;
      });
    }
    case OwnershipConnectionsContextActionTypes.SetOnlyBeneficialOwnership: {
      return produce(state, (draft) => {
        draft.onlyBeneficialOwnership = action.payload;
      });
    }
    case OwnershipConnectionsContextActionTypes.SetIncludeHistoricalOwnership: {
      return produce(state, (draft) => {
        draft.includeHistoricalOwnership = action.payload;
      });
    }
    case OwnershipConnectionsContextActionTypes.NavigateToPath: {
      return produce(state, (draft) => {
        draft.path = draft.path.slice(0, action.payload + 1);
      });
    }
    case OwnershipConnectionsContextActionTypes.PushToPath: {
      return produce(state, (draft) => {
        const lastIdx = draft.path.length;
        if (!lastIdx || draft.path[lastIdx - 1].id !== action.payload.id) {
          draft.path.push(action.payload);
        }
      });
    }
  }
};

export const OwnershipConnectionsContextProvider = ({
  children,
}: OwnershipConnectionsProviderProps) => {
  const [state, dispatch] = useReducer(OwnershipConnectionsReducer, initialConnectionsState);

  return (
    <OwnershipConnectionsContext.Provider value={state}>
      <OwnershipConnectionsDispatchContext.Provider value={dispatch}>
        {children}
      </OwnershipConnectionsDispatchContext.Provider>
    </OwnershipConnectionsContext.Provider>
  );
};

export const useOwnershipConnectionsState = () => {
  const context = useContext(OwnershipConnectionsContext);

  if (context === undefined) {
    throw new Error('OwnershipConnectionsState must be used within a ConnectionsProvider');
  }

  return context;
};

export const useOwnershipConnectionsDispatch = () => {
  const context = useContext(OwnershipConnectionsDispatchContext);

  if (context === undefined) {
    throw new Error(
      'OwnershipConnectionsDispatch must be used within a OwnershipConnectionsProvider',
    );
  }

  return context;
};
